
import { get, find, forEach, mapKeys } from 'lodash'
import Moment from 'moment'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'

const TIMEGAP_ZERO_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:00[Z]'

/**
 * Mixin for utilties dealing with charts and graphs.
 * Requires these additional mixins:
 * - CurrentUserMixin, for 'customerContext'
 * - TemperatureMixin, for 'temperatureToCustomerScale'
 */
var ChartMixin = {
  methods: {
    /**
     * Processes data from a route into a format suitable for the temperature
     * chart. The result will look something like this:
     *
     * {
     *     "air": {
     *         "cooler": {
     *             "temps": ["32.0", "32.0"],
     *             "dates": [1567037280000, 1567063080000]
     *         }
     *     },
     *     "products": {
     *         "44": {
     *             "name": "QIP Lettuce",
     *             "minTemp": "34.0",
     *             "maxTemp": "40.0",
     *             "zone": "cooler",
     *             "simulated": {
     *                 "temps": ["35.1", "35.4"],
     *                 "dates": [1563408180000, 1563434280000]
     *             },
     *             "forecast": {
     *                 "temps": ["35.1", "35.4"],
     *                 "dates": [1563408180000, 1563434280000]
     *             }
     *         }
     *     },
     *     "violations": [],
     *     "routeStart": 1567037009000,
     *     "routeEnd": 1567112913805
     * }
     *
     * @param {RouteRecord} route
     * @param {Object} temperatureData object containing simulated temperatures
     * @param {Object} forecastTemperatureData object containing forecast temperatures
     * @return {Object} The formatted temperature data (see above)
     */
    formatRouteChartData (route, temperatureData, forecastTemperatureData) {
      const chartData = {
        air: {},
        products: {},
        violations: [],
        deliveries: []
      }

      // Some routes have large data gaps that cannot be filled. The gap-fill data processor will ignore any gaps
      // longer than 90 minutes, as this corresponds to the maximum gap allowable for product temperature simulation.
      // Without these gaps filled, the graph appears to show a linearly interpolated value, which can lead users to
      // think the gap-fill algorithm sometimes isn't working, which is not the case.
      //
      // We can deal with this dilemma by inserting a dummy piece of chart data to give the appearance of a gap fill,
      // provided the user has the processing.GapFillVehicle setting enabled.
      if (get(route.customer.getSetting('processing.GapFillVehicle'), 'enabled')) {
        forEach(route.route_violations, (violation) => {
          // Look for data gaps in excess of 90 minutes
          const violationStart = Moment.utc(violation.start)
          const violationEnd = Moment.utc(violation.end)
          if (violation.type === RouteViolationRecord.TYPE_GAP && violationEnd.diff(violationStart, 'minutes') > 90) {
            // Find the data entries for the start and end of the gap
            const gapFill = get(route.customer.getSetting('processing.GapFillVehicle'), 'enabled')
            const endEntry = temperatureData[violationEnd.format(TIMEGAP_ZERO_DATE_FORMAT)]
            if (gapFill && endEntry != null) {
              // Create a new entry one minute after the start of the gap with the end data pulled back
              const newTimestamp = violationStart.add(1, 'minute').format(TIMEGAP_ZERO_DATE_FORMAT)
              temperatureData[newTimestamp] = {
                cooler: endEntry.cooler,
                freezer: endEntry.freezer
              }
            }
          }
        })
      }

      // Sensor data rows key product readings by the stable product_{id}
      // (VF-4177), so index route products the same way. Unlike the old
      // name-based keys, IDs never collide and survive product renames.
      const routeProductsByKey = mapKeys(route.route_products, (rp) => {
        return `product_${rp.product_id}`
      })

      forEach(temperatureData, (temperatureSet, time) => {
        // unix time of the ISO8601 formatted timestamp
        const timestamp = Moment.utc(time).valueOf()

        // Loop through each item in the set (items are product_{id} keys and
        // zones e.g. cooler, freezer)
        forEach(temperatureSet, (temperature, name) => {
          // If the key maps to a route product then chart it as a product
          // series, otherwise treat it as an air/zone series
          const routeProduct = get(routeProductsByKey, name, null)

          if (routeProduct != null) {
            const product = routeProduct.product

            // If this is the first time that we're seeing values for this
            // product we need to set the general attributes we need
            if (!(product.id in chartData.products)) {
              chartData.products[product.id] = this.emptyProductChartEntry(product)
            }
            chartData.products[product.id].simulated.temps.push(this.temperatureToCustomerScale(temperature, 'C'))
            chartData.products[product.id].simulated.dates.push(timestamp)
            chartData.maxSimulatedTime = Math.max(chartData.maxSimulatedTime, timestamp) || timestamp
          } else {
            // Likewise if the air temperature object for this zone is not set
            // up yet then do so.
            if (!(name in chartData.air)) {
              chartData.air[name] = {
                temps: [],
                dates: []
              }
            }
            chartData.air[name].temps.push(this.temperatureToCustomerScale(temperature, 'C'))
            chartData.air[name].dates.push(timestamp)
          }
        })
      })

      forEach(forecastTemperatureData, (temperatures, productId) => {
        // A product only gets an entry in chartData.products above if it has
        // simulated temperatures in temperatureData. A product can legitimately
        // have a forecast but no simulated temperatures yet (e.g. a compartment
        // that hasn't reported any readings so far on an in-progress route), in
        // which case we need to create the entry here or we'd dereference
        // undefined below and take the whole route view down with us. (VF-4310)
        if (!(productId in chartData.products)) {
          const routeProduct = find(route.route_products, (rp) => String(get(rp, 'product.id')) === String(productId))
          // Without the product we have no name/limits to chart against, so
          // there is nothing sensible we can render for this forecast.
          if (get(routeProduct, 'product') == null) {
            return
          }
          chartData.products[productId] = this.emptyProductChartEntry(routeProduct.product)
        }

        forEach(temperatures, (temperatureData, time) => {
          // unix time of the ISO8601 formatted timestamp
          const timestamp = Moment.utc(time).valueOf()

          chartData.products[productId].forecast.low.push(this.temperatureToCustomerScale(temperatureData['95_p_confidence_min'], 'C'))
          chartData.products[productId].forecast.high.push(this.temperatureToCustomerScale(temperatureData['95_p_confidence_max'], 'C'))
          chartData.products[productId].forecast.estimated.push(this.temperatureToCustomerScale(temperatureData.forecast, 'C'))
          chartData.products[productId].forecast.dates.push(timestamp)
          chartData.maxForecastTime = Math.max(chartData.maxForecastTime, timestamp) || timestamp
        })
      })

      if (route.start) {
        chartData.routeStart = Moment.utc(route.start).valueOf()

        // Show DC Departure if vehicle has departed at least 15 minutes after the route start.
        const minuteDifference = 15
        const dcLocation = get(route, 'route_locations[0]')
        if (dcLocation && get(dcLocation, 'location.type') === 'dc' && dcLocation.vehicle_departure !== null) {
          const departureTime = Moment.utc(dcLocation.vehicle_departure)
          if (departureTime.diff(Moment.utc(route.start)) > minuteDifference * 60 * 1000) {
            chartData.dcDeparture = departureTime.valueOf()
          }
        }
      }

      if (route.end) {
        chartData.routeEnd = Moment.utc(route.end).valueOf()
      }

      // Find route violations
      forEach(route.route_violations, (violation) => {
        chartData.violations.push({
          start: Moment(violation.start).valueOf(),
          end: Moment(violation.end).valueOf(),
          zone: violation.zone,
          type: violation.type
        })
      })

      // Include each delivery so that it can be rendered on the chart if desired
      forEach(route.route_locations, (routeLocation) => {
        // If this was a planned (but missed stop) or not a delivery location
        // (e.g. ignored location or the DC) then do not include it, we're only
        // rendering actual deliveries on the chart
        if (routeLocation.vehicle_arrival == null || !routeLocation.location.isDeliveryLocation()) {
          return
        }

        chartData.deliveries.push({
          start: Moment(routeLocation.vehicle_arrival).valueOf(),
          end: Moment(routeLocation.vehicle_departure).valueOf(),
          label: routeLocation.location.label,
          planned: routeLocation.eta != null
        })
      })

      // Provide a simple accessor to identify if there are any deliveries to be
      // rendered.
      chartData.hasDeliveries = () => chartData.deliveries.length > 0

      return chartData
    },

    /**
     * Build an empty chartData.products entry for a product, ready to have
     * simulated and/or forecast temperatures pushed onto it.
     *
     * @param {Object} product the product (from a route product)
     * @return {Object} the empty entry
     */
    emptyProductChartEntry (product) {
      return {
        name: product.name,
        minTemp: this.temperatureToCustomerScale(product.min_temp, product.temperature_scale),
        maxTemp: this.temperatureToCustomerScale(product.max_temp, product.temperature_scale),
        zone: product.zone,
        simulated: {
          temps: [],
          dates: []
        },
        forecast: {
          low: [],
          high: [],
          estimated: [],
          dates: []
        }
      }
    }

  }

}

export default ChartMixin
